/**
 * NavDrawer — full-page left-slide navigation drawer that REPLACES
 * the old vertical left rail (2026-07-14 rev · user-approved after
 * mockup at /mockups/nav-drawer.html).
 *
 * Behavior:
 *   • Controlled via `open` / `onClose` (parent-owned).
 *   • Backdrop dim on the right; tapping it closes.
 *   • Slide-in transform, ~320ms cubic-bezier for smoothness.
 *   • Locks body scroll while open.
 *   • Route change closes it automatically.
 *   • Overlays the page — no permanent content shift.
 *
 * Structure (matches the mockup):
 *   TravelBio logo (top)
 *   Home · Explore · Bookmarks · Booked Trips · Dream
 *   Chapters (expandable — years, fetched on first expand)
 *   Passport (expandable — Stamps / Badges / Rewards anchors)
 *   Lounge
 *   Marketplace (expandable — Travel Insurance · Amazon Shopping Lists)
 *
 * Fonts: Playfair Display (headings), Kalam/Caveat (hand labels),
 * Outfit (UI). All already loaded — no new fonts.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Home, Compass, Bookmark, Luggage, Star, BookOpen, Award, Umbrella,
  ShoppingBag, Shield, PackageOpen, ChevronDown, X as XIcon, BedDouble,
} from "lucide-react";
import { api } from "@/lib/api";

const LOGO_URL = "/assets/logo-landscape.png";

const TOP_ITEMS_BEFORE_EXPLORE = [
  { to: "/home", label: "Home", Icon: Home, testId: "drawer-item-home" },
];

const EXPLORE_SUBS = [
  { to: "/bookmarks", label: "Bookmarks",    Icon: Bookmark, testId: "drawer-explore-bookmarks" },
  // Phase 2 merge (2026-07-16): "Booked Trips" nav link removed —
  // trips now render inside their chapter year's timeline. The
  // `/booked` route redirects to /chapters/{currentYear} so any
  // legacy bookmarks still land on something sensible.
];

const TOP_ITEMS_AFTER_EXPLORE = [
  { to: "/dream", label: "Dream", Icon: Star, testId: "drawer-item-dream" },
];

const PASSPORT_SUBS = [
  { hash: "stamps",  label: "Stamps",  testId: "drawer-passport-stamps" },
  { hash: "badges",  label: "Badges",  testId: "drawer-passport-badges" },
  { hash: "rewards", label: "Rewards", testId: "drawer-passport-rewards" },
];

const MARKETPLACE_SUBS = [
  { to: "/marketplace/shop",             label: "TravelBio Shop",        Icon: ShoppingBag, testId: "drawer-marketplace-shop" },
  { to: "/marketplace/travel-insurance", label: "Travel Insurance",     Icon: Shield,      testId: "drawer-marketplace-insurance" },
  { to: "/marketplace/shopping-lists",   label: "Amazon Shopping Lists", Icon: PackageOpen, testId: "drawer-marketplace-shopping" },
];

function useChapterYears(open, activeGroup) {
  // Fetch the user's chapter years the first time the Chapters group
  // expands. Cached until page reload — no need to re-fetch each open.
  const [years, setYears] = useState(null);
  useEffect(() => {
    if (!open || activeGroup !== "chapters" || years !== null) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/chapters");
        if (!alive) return;
        const raw = Array.isArray(data) ? data : (data?.results || []);
        const uniq = new Set();
        raw.forEach((c) => {
          const y = c?.year ?? c?.chapter_year ?? c?.start_year;
          if (y) uniq.add(Number(y));
        });
        const sorted = Array.from(uniq).sort((a, b) => b - a);
        setYears(sorted);
      } catch {
        setYears([]);
      }
    })();
    return () => { alive = false; };
  }, [open, activeGroup, years]);
  return years;
}

export default function NavDrawer({ open, onClose }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [expanded, setExpanded] = useState(null); // 'explore' | 'marketplace' | null
  // 2026-07-17: `useChapterYears` no longer wired — Chapters is a
  // single-tap leaf now. Hook + `PASSPORT_SUBS` left in the file
  // in case we resurrect drawer expansion in a future feature.
  // eslint-disable-next-line no-unused-vars
  const _years = useChapterYears(open, expanded === "chapters" ? "chapters" : null);

  // Auto-close on route change.
  useEffect(() => {
    onClose && onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);

  // Body scroll lock while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const toggleExpanded = useCallback((key) => {
    setExpanded((prev) => (prev === key ? null : key));
  }, []);

  const goto = useCallback((path) => {
    onClose && onClose();
    // Slight delay so the drawer animation kicks off before the
    // main content re-renders under it — feels snappier.
    setTimeout(() => nav(path), 60);
  }, [nav, onClose]);

  const isActive = useMemo(() => {
    return (path) => loc.pathname === path || loc.pathname.startsWith(path + "/");
  }, [loc.pathname]);

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="nav-drawer-backdrop"
        aria-hidden={!open}
        onClick={onClose}
        className={
          "fixed inset-0 z-[70] bg-tb-navy/55 backdrop-blur-[2px] transition-opacity duration-300 " +
          (open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none")
        }
      />
      {/* Drawer */}
      <aside
        data-testid="nav-drawer"
        role="navigation"
        aria-label="Main navigation"
        aria-hidden={!open}
        className={
          "fixed top-0 left-0 bottom-0 z-[80] w-[82%] max-w-[320px] bg-tb-navy-deep text-tb-cream " +
          "border-r border-tb-gold/30 shadow-[12px_0_28px_-8px_rgba(0,0,0,0.4)] " +
          "flex flex-col transition-transform duration-300 will-change-transform " +
          (open ? "translate-x-0" : "-translate-x-full")
        }
      >
        {/* Header — TravelBio logo + close */}
        <div className="shrink-0 flex items-center justify-between px-5 pt-5 pb-4 border-b border-tb-gold/25">
          <button
            type="button"
            aria-label="Go home"
            onClick={() => goto("/home")}
            className="tb-tap select-none"
          >
            <img
              src={LOGO_URL}
              alt="TravelBio"
              width={148}
              height={72}
              className="block"
              style={{ height: 62, width: "auto", objectFit: "contain" }}
              draggable={false}
            />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            data-testid="nav-drawer-close"
            className="w-9 h-9 rounded-full border border-tb-gold/40 bg-tb-navy/70 hover:bg-tb-navy flex items-center justify-center tb-tap"
          >
            <XIcon className="w-4 h-4 text-tb-cream" strokeWidth={2} />
          </button>
        </div>

        {/* Menu body */}
        <div className="flex-1 min-h-0 overflow-y-auto py-3">
          {TOP_ITEMS_BEFORE_EXPLORE.map(({ to, label, Icon, testId }) => (
            <DrawerLeaf
              key={to}
              to={to}
              label={label}
              Icon={Icon}
              testId={testId}
              active={isActive(to)}
              onGo={goto}
            />
          ))}

          {/* Explore (expandable — Bookmarks + Booked Trips nested) */}
          <DrawerGroup
            groupKey="explore"
            label="Explore"
            Icon={Compass}
            testId="drawer-item-explore"
            expanded={expanded === "explore"}
            onToggle={() => toggleExpanded("explore")}
            onGoTop={() => goto("/explore")}
            active={loc.pathname === "/explore" || loc.pathname.startsWith("/explore/")}
          >
            {EXPLORE_SUBS.map((s) => (
              <SubLeaf
                key={s.to}
                label={s.label}
                Icon={s.Icon}
                onGo={() => goto(s.to)}
                testId={s.testId}
              />
            ))}
          </DrawerGroup>

          {TOP_ITEMS_AFTER_EXPLORE.map(({ to, label, Icon, testId }) => (
            <DrawerLeaf
              key={to}
              to={to}
              label={label}
              Icon={Icon}
              testId={testId}
              active={isActive(to)}
              onGo={goto}
            />
          ))}

          {/* 2026-07-17 — Chapters + Passport are now single-tap
              leaves (no sub-items). Tapping either heading routes
              directly to `/chapters/{currentYear}` (via redirect)
              or `/passport`. Years used to fan out under Chapters
              and Stamps/Badges/Rewards anchors under Passport;
              both were removed per user request to declutter the
              drawer. `useChapterYears` + `PASSPORT_SUBS` are left
              in the file un-referenced so a future feature can
              restore the expansion if needed. */}
          <DrawerLeaf
            to="/chapters"
            label="Chapters"
            Icon={BookOpen}
            testId="drawer-item-chapters"
            active={isActive("/chapters")}
            onGo={goto}
          />

          <DrawerLeaf
            to="/passport"
            label="Passport"
            Icon={Award}
            testId="drawer-item-passport"
            active={isActive("/passport")}
            onGo={goto}
          />

          <DrawerLeaf
            to="/lounge"
            label="Lounge"
            Icon={Umbrella}
            testId="drawer-item-lounge"
            active={isActive("/lounge")}
            onGo={goto}
          />

          {/* Batch 2 (2026-08-07): "Book a Hotel" leaf removed from the
              drawer per traveler-side scoping. The /hotels route stays
              registered so cert_reviewer's Hotelbeds cert flow +
              deep-links keep working; travelers reach stay search via
              the header magnifier icon (→ /hotels → StaySearchPage). */}

          {/* Marketplace (expandable — new) */}
          <div className="mx-3 my-2 border-t border-tb-gold/15" />
          <div className="px-5 py-1.5 font-hand text-tb-gold text-[10px] uppercase tracking-[0.2em]">
            Marketplace
          </div>
          <DrawerGroup
            groupKey="marketplace"
            label="Marketplace"
            Icon={ShoppingBag}
            testId="drawer-item-marketplace"
            expanded={expanded === "marketplace"}
            onToggle={() => toggleExpanded("marketplace")}
            onGoTop={() => toggleExpanded("marketplace")}
            active={loc.pathname.startsWith("/marketplace")}
          >
            {MARKETPLACE_SUBS.map((s) => (
              <SubLeaf
                key={s.to}
                label={s.label}
                Icon={s.Icon}
                onGo={() => goto(s.to)}
                testId={s.testId}
              />
            ))}
          </DrawerGroup>
        </div>

        <div className="shrink-0 px-5 py-3 border-t border-tb-gold/25 font-hand text-tb-gold/70 text-[11px] tracking-widest text-center">
          your lifelong travel journal
        </div>
      </aside>
    </>
  );
}

function DrawerLeaf({ to, label, Icon, testId, active, onGo }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onGo(to)}
      className={
        "w-full flex items-center gap-3 px-5 py-3 border-l-4 transition-colors tb-tap text-left " +
        (active
          ? "bg-tb-gold/10 border-tb-gold text-tb-gold-bright"
          : "border-transparent text-tb-cream hover:bg-white/5")
      }
    >
      <Icon className="w-5 h-5 shrink-0" strokeWidth={active ? 2.2 : 1.75} />
      <span className={"font-serif " + (active ? "italic" : "")} style={{ fontFamily: '"Playfair Display", serif', fontSize: "1.05rem" }}>
        {label}
      </span>
    </button>
  );
}

function DrawerGroup({
  groupKey, label, Icon, testId, expanded, onToggle, onGoTop, active, children,
}) {
  return (
    <div>
      <div className="flex items-stretch">
        <button
          type="button"
          data-testid={testId}
          onClick={onGoTop || onToggle}
          className={
            "flex-1 flex items-center gap-3 px-5 py-3 border-l-4 transition-colors tb-tap text-left " +
            (active
              ? "bg-tb-gold/10 border-tb-gold text-tb-gold-bright"
              : "border-transparent text-tb-cream hover:bg-white/5")
          }
        >
          <Icon className="w-5 h-5 shrink-0" strokeWidth={active ? 2.2 : 1.75} />
          <span style={{ fontFamily: '"Playfair Display", serif', fontSize: "1.05rem" }} className={active ? "italic" : ""}>
            {label}
          </span>
        </button>
        <button
          type="button"
          data-testid={`${testId}-toggle`}
          onClick={onToggle}
          aria-label={`${expanded ? "Collapse" : "Expand"} ${label}`}
          aria-expanded={expanded}
          className="px-4 flex items-center text-tb-gold hover:text-tb-gold-bright tb-tap"
        >
          <ChevronDown
            className={"w-4 h-4 transition-transform duration-200 " + (expanded ? "rotate-180" : "")}
            strokeWidth={2}
          />
        </button>
      </div>
      <div
        className={
          "overflow-hidden transition-all duration-200 " +
          (expanded ? "max-h-[420px]" : "max-h-0")
        }
      >
        <div className="bg-black/20 py-1">
          {children}
        </div>
      </div>
    </div>
  );
}

function SubLeaf({ label, onGo, testId, Icon, muted = false }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onGo}
      className={
        "w-full flex items-center gap-2 pl-14 pr-5 py-2 text-left tb-tap " +
        (muted
          ? "text-tb-cream/50 cursor-default"
          : "text-tb-cream/90 hover:text-tb-gold-bright hover:bg-white/5")
      }
    >
      <span className="w-1.5 h-1.5 rounded-full bg-tb-gold/70 shrink-0" aria-hidden="true" />
      {Icon && <Icon className="w-4 h-4 text-tb-gold/85 shrink-0" strokeWidth={1.75} />}
      <span style={{ fontFamily: '"Outfit", sans-serif', fontSize: "0.92rem" }}>
        {label}
      </span>
    </button>
  );
}
